import React, { useState, useEffect, useCallback } from 'react';
import { Search, Package, RefreshCw, Filter, ArrowUpDown, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { productsAPI, brandsAPI, categoriesAPI } from '@/services/api';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

const WarehouseProductsPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const warehouseId = user?.entity_id;
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  
  // Dropdowns
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Load dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [brandsRes, catsRes] = await Promise.all([
          brandsAPI.list({ is_active: true }),
          categoriesAPI.list({ is_active: true })
        ]);
        setBrands(brandsRes.data?.items || brandsRes.data || []);
        setCategories(catsRes.data?.items || catsRes.data || []);
      } catch (error) {
        console.error('Error loading dropdowns:', error);
      }
    };
    loadDropdowns();
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        page: pagination.page, 
        page_size: 50,
        is_active: true,
        warehouse_id: warehouseId
      };
      if (search) params.search = search;
      if (brandFilter !== 'all') params.brand_id = brandFilter;
      if (categoryFilter !== 'all') params.category_id = categoryFilter;
      
      const response = await productsAPI.list(params);
      const data = response.data || response;
      setProducts(data.items || data || []);
      if (data.total !== undefined) {
        setPagination(prev => ({
          ...prev,
          total: data.total,
          total_pages: data.total_pages || 1
        }));
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load products', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, search, brandFilter, categoryFilter, warehouseId, toast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Search on enter
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPagination(p => ({ ...p, page: 1 }));
      fetchProducts();
    }
  };

  // Download server-rendered PDF with current filters applied.
  const handleExportPdf = async () => {
    if (!warehouseId) return;
    try {
      const params = {
        warehouse_id: warehouseId,
        is_active: true,
        ...(search ? { search } : {}),
        ...(brandFilter !== 'all' ? { brand_id: brandFilter } : {}),
        ...(categoryFilter !== 'all' ? { category_id: categoryFilter } : {}),
      };
      const res = await api.get('/warehouse/export/products-pdf', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `warehouse-products-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'PDF export failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="warehouse-products">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Products</h1>
          <p className="text-sm text-gray-500">Browse all active products available for ordering</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPdf} data-testid="products-export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={fetchProducts}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="Search by name, SKU, barcode..."
                className="pl-10"
              />
            </div>
            <Select value={brandFilter} onValueChange={(v) => { setBrandFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No products found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>SKU</TableHead>
                  <TableHead>Product Name</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>HSN</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead className="text-right">MRP</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Stock Qty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map(product => {
                  // Live stock for this warehouse (from stock_entries via the API)
                  const stockQty = product.stock_qty ?? product.current_stock?.[warehouseId] ?? 0;
                  
                  return (
                    <TableRow key={product.id} className="hover:bg-gray-50">
                      <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{product.name}</span>
                          {product.barcodes?.length > 0 && (
                            <span className="text-xs text-gray-400">
                              Barcode: {product.barcodes[0]?.barcode}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{product.brand_name || '-'}</TableCell>
                      <TableCell>{product.category_name || '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{product.hsn_code || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{product.base_uom || 'PCS'}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {product.mrp ? `₹${product.mrp.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell className="text-right text-gray-600">
                        {product.cost_price ? `₹${product.cost_price.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={`font-medium ${stockQty > 0 ? 'text-green-600' : 'text-red-500'}`}
                          data-testid={`wh-product-stock-${product.id}`}
                        >
                          {stockQty} {product.base_uom || 'PCS'}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing {products.length} of {pagination.total} products
        </p>
        {pagination.total_pages > 1 && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-sm text-gray-600">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button 
              variant="outline"
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.total_pages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehouseProductsPage;
